/**
 * "Giao thông" tab of the info panel: road-planning analysis along the route at the picked point
 * (planned right-of-way, widening, buildings inside the planned road land, new roads).
 * Rendering only — the computation lives in core/roadplan.js, the data loaders in roaddata.js.
 */
import { esc, icon, fmt } from './ui.js';

export const ROAD_STATUS = {
  keep: { label: 'Giữ nguyên – không vướng công trình', color: '#16a34a' },
  widen: { label: 'Mở rộng – chỉ giới cắt vào công trình', color: '#f59e0b' },
  incell: { label: 'Đường hiện có nằm trong ô đất (không phải đất GT)', color: '#8b5cf6' },
  junction: { label: 'Nút giao / quảng trường', color: '#64748b' },
  open: { label: 'Chưa xác định được hai bên', color: '#94a3b8' },
  none: { label: 'Ngoài phạm vi đồ án', color: '#cbd5e1' },
};
const NEW_ROUTE = { label: 'Tuyến mới (hiện chưa có đường)', color: '#2563eb' };
const NEW_LABEL = { keep: 'Không vướng công trình', widen: 'Chỉ giới cắt vào công trình' };
const stLabel = (r, k) => (r.kind === 'new' && NEW_LABEL[k]) || ROAD_STATUS[k]?.label || k;
const LEVEL_SHORT = { detail: 'QH chi tiết 1/500', zone: 'QH phân khu', general: 'QH chung 1/10.000' };
const n1 = (v) => fmt.num(v, v < 20 ? 1 : 0);
const pctOf = (a, b) => (b ? Math.round((a / b) * 100) : 0);

/* ---------------- map overlay ---------------- */

/** GeoJSON for the map: right-of-way band, red lines, affected buildings, today's centreline by status. */
export function roadOverlay(r) {
  if (!r?.geojson) return null;
  return r.geojson;
}

/** Width tag position + text for the station closest to the pick. */
export function roadTag(r) {
  const st = midStation(r);
  if (!st?.W || !st.pL || !st.pR) return null;
  const far = st.dL >= st.dR ? st.pL : st.pR;
  return { ll: { lng: far[0], lat: far[1] }, html: `${icon('move-horizontal')}≈ ${n1(st.W)} m` };
}
function midStation(r) {
  const sts = r?.stations || [];
  let best = null;
  for (const s of sts) if (s.W != null && (s.status === 'keep' || s.status === 'widen') && (!best || Math.abs(s.s) < Math.abs(best.s))) best = s;
  return best && Math.abs(best.s) <= 60 ? best : null;
}

/* ---------------- tab ---------------- */

export function roadLoading() {
  return `<section class="hero road" style="--sw:var(--accent);--i:0">
    <div class="hero-top"><span class="card-ico road-ico">${icon('route')}</span><div style="flex:1;min-width:0"><div class="sk sk-title"></div><div class="sk sk-line" style="width:60%"></div></div></div>
    <div class="metrics"><div class="sk sk-block" style="height:56px"></div><div class="sk sk-block" style="height:56px"></div><div class="sk sk-block" style="height:56px"></div></div>
    <div class="snap"><div class="snap-loading">${icon('loader-circle', 'spin')}<span>Đang tải tim đường hiện trạng, công trình và ranh giới ô đất quy hoạch quanh điểm chọn…</span></div></div>
  </section>
  <div class="sk sk-block" style="height:150px;--i:1"></div>
  <div class="sk sk-block" style="height:90px;--i:2"></div>`;
}

export function roadError(msg) {
  return `<div class="note warn">${icon('triangle-alert')}<div><b>Không phân tích được quy hoạch giao thông.</b><br>${esc(msg || '')}<div style="margin-top:8px"><button type="button" class="btn sm" data-act="road-retry">${icon('rotate-ccw')}Thử lại</button></div></div></div>`;
}

/** The whole tab. stagger() is applied by the caller. */
export function roadTab(r) {
  if (r.kind === 'none') return [noneHero(r), disclaimer(r)];
  if (r.kind === 'parcel') return [parcelHero(r), disclaimer(r)];
  return [routeHero(r), parcelBlock(r), profileCard(r), detailCard(r), buildingsCard(r), legendCard(r), disclaimer(r)];
}

function badge(tone, ic, text, tip) {
  return `<span class="badge ${tone}"${tip ? ` data-tip="${esc(tip)}"` : ''}>${ic ? icon(ic) : ''}${esc(text)}</span>`;
}
const VERDICT_ICON = { generalonly: 'circle-question-mark', widen: 'construction', side: 'split', construction: 'construction', new: 'spline', enough: 'circle-check', clear: 'circle-check', offplan: 'route-off', lane: 'signpost', corridor: 'waypoints', unclear: 'circle-question-mark', nodata: 'circle-question-mark' };

function routeHero(r) {
  const S = r.summary, v = r.verdict, B = r.buildings;
  const title = r.road?.name || (r.kind === 'new' ? 'Tuyến đường quy hoạch mới' : 'Tuyến đường chưa có tên');
  const sub = [r.kind === 'new' && v.key !== 'side' ? 'Đất giao thông quy hoạch, hiện chưa có đường' : r.road?.classLabel, S.mainLevel ? 'theo ' + LEVEL_SHORT[S.mainLevel] : null].filter(Boolean).join(' · ');
  const badges = [badge(v.tone === 'violet' ? 'violet' : v.tone === 'muted' ? '' : v.tone, VERDICT_ICON[v.key], v.label)];
  if (r.road?.construction && v.key !== 'construction') badges.push(badge('warn', 'construction', 'Đang thi công'));
  badges.push(badge('', 'gauge', `Độ tin cậy ${r.confidence === 'medium' ? 'trung bình' : 'thấp'}`, r.confidence === 'medium' ? 'Ranh giới ô đất QHPK/QHCT kết hợp dữ liệu công trình' : S.mainLevel === 'general' ? 'Chỉ có quy hoạch chung tỷ lệ 1/10.000' : 'Thiếu dữ liệu công trình hoặc đồ án chi tiết'));
  const m = [];
  const W = S.width, E = S.existing;
  m.push(metric('Lộ giới quy hoạch', W ? `≈ ${n1(W.median)}<small>m</small>` : '—', W && W.max - W.min > 4 ? `dao động ${n1(W.min)}–${n1(W.max)} m` : W ? 'giữa hai chỉ giới đường đỏ' : 'chưa đo được'));
  if (r.kind === 'new' && v.key !== 'side') m.push(metric('Chưa có đường', `${Math.round((r.newShare ?? 0) * 100)}<small>%</small>`, 'chiều dài đoạn phân tích'));
  else m.push(metric('Hiện trạng', E ? `≈ ${n1(E.median)}<small>m</small>` : '—', E ? 'giữa hai dãy công trình' : r.footprints == null ? 'không tải được dữ liệu nhà' : 'chưa đo được'));
  if (S.mainLevel === 'general') m.push(metric('Công trình vướng', '—', 'QH chung: không đánh giá'));
  else if (B) m.push(metric('Công trình vướng', `${B.significant}`, B.areaIn ? B.significant ? `~${fmt.num(B.areaIn)} m² trong lộ giới` : `chỉ chạm nhẹ, tổng ~${fmt.num(B.areaIn)} m²` : 'không có phần nằm trong lộ giới'));
  else m.push(metric('Công trình vướng', '—', 'không tải được dữ liệu nhà'));
  return `<section class="hero road" data-hero="road" style="--sw:${toneColor(v.tone)}">
    <div class="hero-top">
      <span class="card-ico road-ico">${icon(r.kind === 'new' ? 'spline' : 'route')}</span>
      <div style="min-width:0">
        <div class="hero-title">${esc(title)}</div>
        <div class="hero-sub">${esc(sub)}</div>
        <div class="badges">${badges.join('')}</div>
      </div>
    </div>
    ${W || B?.count ? `<div class="metrics">${m.join('')}</div>` : ''}
    <p class="road-say">${explain(r)}</p>
  </section>`;
}
function metric(k, v, s) {
  return `<div class="metric"><div class="k">${esc(k)}</div><div class="v">${v}</div>${s ? `<div class="s">${esc(s)}</div>` : ''}</div>`;
}
const toneColor = (t) => ({ warn: 'var(--warn)', ok: 'var(--ok)', accent: 'var(--accent)', violet: '#8b5cf6' }[t] || 'var(--text-3)');

/** One-paragraph plain-language reading of the result. */
function explain(r) {
  const S = r.summary, v = r.verdict, B = r.buildings, W = S.width, E = S.existing;
  const span = `đoạn ${fmt.num(Math.abs(r.span.from) + r.span.to)} m quanh điểm chọn`;
  const wTxt = W ? `<b>≈ ${n1(W.median)} m</b>` : '';
  const ww = W ? ` ${wTxt}` : ''; // width not measured: the sentences read without it
  const sides = [];
  if (S.encL > 0) sides.push(`phía ${S.dirL} ~${n1(S.encL)} m`);
  if (S.encR > 0) sides.push(`phía ${S.dirR} ~${n1(S.encR)} m`);
  const bTxt = B && B.significant ? ` Khoảng <b>${B.significant} công trình</b> có phần diện tích nằm trong lộ giới (tổng ~${fmt.num(B.areaIn)} m²).` : '';
  switch (v.key) {
    case 'widen': return `Theo đồ án, ${span} có lộ giới${W ? ` ${wTxt},` : ''} rộng hơn hiện trạng${E ? ` (~${n1(E.median)} m giữa hai dãy nhà)` : ''}: chỉ giới đường đỏ cắt vào công trình ở <b>${pctOf(S.counts.widen, S.counts.keep + S.counts.widen)}%</b> chiều dài${sides.length ? `, lấn vào ${sides.join(', ')}` : ''}.${bTxt}`;
    case 'enough': return `Khoảng cách giữa hai dãy công trình hiện có (~${n1(E.median)} m) đã xấp xỉ hoặc lớn hơn lộ giới quy hoạch${ww}: tuyến cơ bản giữ nguyên, ít phải giải phóng mặt bằng.${bTxt}`;
    case 'clear': return `${W ? `Lộ giới quy hoạch ${wTxt}; chỉ` : 'Chỉ'} giới đường đỏ hầu như không cắt vào công trình hiện có trên ${span}${E ? '' : ' (hai bên chủ yếu là đất trống hoặc thiếu dữ liệu nhà)'}.${bTxt}`;
    case 'construction': return `Tuyến đang được thi công theo dữ liệu OpenStreetMap.${W ? ` Lộ giới theo đồ án ${wTxt}` : ''}${sides.length ? `${W ? '; so' : ' So'} với hiện trạng, phạm vi mở rộng chủ yếu ${sides.join(', ')}` : ''}${W || sides.length ? '.' : ''}${bTxt}`;
    case 'corridor': return `Khu vực chỉ có quy hoạch chung tỷ lệ 1/10.000 (chưa có đồ án phân khu số hoá). Hành lang tuyến${ww} chỉ mang tính định hướng, chưa đủ chính xác để xác định nhà bị ảnh hưởng.`;
    case 'offplan': return `Phần lớn tuyến hiện có nằm bên trong các ô đất quy hoạch chứ không trên đất giao thông: đoạn này có thể được nắn tuyến, thu hẹp hoặc không được giữ trong đồ án.${S.offset ? ` Đất giao thông quy hoạch gần nhất lệch ~${n1(S.offset.d)} m về phía ${S.offset.dir}.` : ''}`;
    case 'generalonly': return 'Khu vực chỉ có quy hoạch chung tỷ lệ 1/10.000 (đồ án phân khu chưa được số hoá trên hệ thống), vốn chỉ thể hiện các trục đường chính. Tuyến này nằm trong ô đất của quy hoạch chung nên chưa xác định được lộ giới; cần tra cứu đồ án phân khu hoặc chỉ giới đường đỏ tại cơ quan quản lý.';
    case 'lane': return 'Ngõ / đường nhỏ: đồ án phân khu không thể hiện các ngõ nên điểm này nằm trong ô đất. Lộ giới ngõ được xác định theo quy hoạch chi tiết hoặc quy định riêng của quận, huyện.';
    case 'side': return `Đất giao thông quy hoạch${ww} chủ yếu trùng với tuyến hiện có nhưng mở rộng lệch về một phía hoặc nắn lại hướng tuyến${sides.length ? ` (lấn ${sides.join(', ')})` : ''}.${bTxt}`;
    case 'new': return `Đồ án dành${ww} đất cho một tuyến đường mà hiện chưa có (~${Math.round((r.newShare ?? 0) * 100)}% ${span} chưa có đường).${bTxt || (B ? ' Chưa thấy công trình nằm trong phạm vi tuyến.' : '')}`;
    case 'unclear': return 'Không xác định được hai chỉ giới đường đỏ ở hai bên tuyến (khu vực rìa đồ án hoặc nút giao lớn).';
    default: return 'Khu vực chưa có đồ án quy hoạch phân khu / chi tiết đã số hoá trên hệ thống.';
  }
}

/** What the pick itself means when it fell inside a plot beside the road. */
function parcelBlock(r) {
  const p = r.parcel;
  if (!p?.cell) return '';
  const b = p.building;
  const hit = b && b.areaIn > 0;
  return `<div class="note${hit ? ' warn' : ''}">${icon(hit ? 'triangle-alert' : 'map-pin')}<div>Điểm chọn thuộc ô <b>${esc(p.cell.name || 'đất quy hoạch')}</b>, cách chỉ giới đường đỏ gần nhất ~${n1(p.edgeDist)} m.${b ? hit ? ` Công trình tại điểm (~${fmt.num(b.area)} m²) có <b>~${fmt.num(b.areaIn, 1)} m² (${Math.round(b.share * 100)}%)</b> nằm trong đất giao thông quy hoạch.` : ` Công trình tại điểm (~${fmt.num(b.area)} m²) không nằm trong đất giao thông quy hoạch.` : ''}</div></div>`;
}

function parcelHero(r) {
  const p = r.parcel, b = p.building;
  const hit = b && b.areaIn > 0;
  const m = [metric('Cách chỉ giới đường đỏ', p.edgeDist != null ? `${n1(p.edgeDist)}<small>m</small>` : '—', 'ranh ô đất gần nhất')];
  if (b) {
    m.push(metric('Công trình tại điểm', `${fmt.num(b.area)}<small>m²</small>`, 'diện tích chiếm đất'));
    m.push(metric('Nằm trong đất GT', `${fmt.num(b.areaIn, 1)}<small>m²</small>`, `${Math.round(b.share * 100)}% công trình`));
  }
  return `<section class="hero road" data-hero="road" style="--sw:${hit ? 'var(--warn)' : 'var(--ok)'}">
    <div class="hero-top">
      <span class="card-ico road-ico">${icon('land-plot')}</span>
      <div style="min-width:0">
        <div class="hero-title">${hit ? 'Một phần công trình nằm trong đất giao thông' : 'Điểm chọn không nằm trên đất giao thông'}</div>
        <div class="hero-sub">Thuộc ô ${esc(p.cell?.name || 'đất quy hoạch')} · ${esc(r.plan.levelLabel || '')}</div>
        <div class="badges">${badge(hit ? 'warn' : 'ok', hit ? 'triangle-alert' : 'circle-check', hit ? 'Có thể bị ảnh hưởng khi làm đường' : 'Không vướng lộ giới')}</div>
      </div>
    </div>
    <div class="metrics">${m.join('')}</div>
    <p class="road-say">${p.level === 'general' ? 'Chỉ có quy hoạch chung 1/10.000 tại đây: khoảng cách tới ranh ô đất chỉ mang tính định hướng. ' : ''}Không có tuyến đường hiện trạng nào trong bán kính 30 m. Chạm vào lòng đường (hoặc khoảng trống giữa các ô đất) để xem quy hoạch cả tuyến: lộ giới, đoạn mở rộng và công trình bị ảnh hưởng.</p>
  </section>`;
}

function noneHero(r) {
  const has = r.plan.level;
  return `<section class="hero" data-hero="empty">
    <div class="hero-top">
      <span class="card-ico" style="width:40px;height:40px">${icon('route-off')}</span>
      <div><div class="hero-title">${has ? 'Không tìm thấy tuyến đường gần điểm chọn' : 'Chưa có đồ án quy hoạch chi tiết tại đây'}</div>
      <div class="hero-sub">${has ? 'Không có đường hiện trạng trong bán kính 30 m và điểm chọn không nằm trên đất giao thông quy hoạch. Hãy chạm gần tim đường hơn.' : 'Khu vực chưa có quy hoạch phân khu / chi tiết số hoá: không xác định được chỉ giới đường đỏ. Thử bật lớp Quy hoạch chung để xem định hướng mạng lưới đường.'}</div></div>
    </div>
  </section>`;
}

/* ---------------- along-route profile ---------------- */

const PW = 320, PH = 156, PAD = { l: 34, r: 8, t: 8, b: 28 };
/**
 * Straightened plan view of the corridor: today's centreline is the horizontal axis, the planned
 * red lines are drawn at their offsets on each side, existing buildings fill the space beyond their
 * front line, and the part of the buildings inside the planned road land is highlighted.
 */
function profileCard(r) {
  const sts = r.stations || [];
  if (sts.length < 3 || !r.summary.width) return '';
  const S = r.summary;
  const x0 = sts[0].s, x1 = sts[sts.length - 1].s;
  const on = (st) => st.status === 'keep' || st.status === 'widen';
  const sides = sts.filter(on).flatMap((st) => [st.dL, st.dR]).filter((v) => v != null).sort((a, b) => a - b);
  const p90 = sides.length ? sides[Math.floor(sides.length * 0.9)] : 20;
  const ymax = niceMax(Math.max(8, p90 * 1.35));
  const H = PH - PAD.t - PAD.b, mid = PAD.t + H / 2;
  const X = (v) => PAD.l + ((v - x0) / Math.max(1, x1 - x0)) * (PW - PAD.l - PAD.r);
  const Y = (o) => mid - (Math.max(-ymax, Math.min(ymax, o)) / ymax) * (H / 2);
  const half = (i) => [i ? (X(sts[i - 1].s) + X(sts[i].s)) / 2 : X(sts[i].s), i < sts.length - 1 ? (X(sts[i].s) + X(sts[i + 1].s)) / 2 : X(sts[i].s)];
  let band = '', bld = '', enc = '';
  sts.forEach((st, i) => {
    const [a, b] = half(i), w = Math.max(0.6, b - a).toFixed(1), xa = a.toFixed(1);
    if (on(st) && st.dL != null && st.dR != null) band += `<rect x="${xa}" y="${Y(st.dL).toFixed(1)}" width="${w}" height="${(Y(-st.dR) - Y(st.dL)).toFixed(1)}"/>`;
    for (const [sg, bv, dv] of [[1, st.bL, st.dL], [-1, st.bR, st.dR]]) {
      if (bv == null) continue;
      const y1 = Y(sg * bv), y2 = Y(sg * ymax);
      bld += `<rect x="${xa}" y="${Math.min(y1, y2).toFixed(1)}" width="${w}" height="${Math.abs(y2 - y1).toFixed(1)}"/>`;
      if (on(st) && dv != null && bv < dv - 0.3) { const e2 = Y(sg * dv); enc += `<rect x="${xa}" y="${Math.min(y1, e2).toFixed(1)}" width="${w}" height="${Math.abs(e2 - y1).toFixed(1)}"/>`; }
    }
  });
  const red = (key, sg) => {
    let d = '', pen = false;
    for (const st of sts) { const v = on(st) ? st[key] : null; if (v == null) { pen = false; continue; } d += `${pen ? 'L' : 'M'}${X(st.s).toFixed(1)},${Y(sg * v).toFixed(1)}`; pen = true; }
    return d;
  };
  const tick = (o) => `<line x1="${PAD.l}" x2="${PW - PAD.r}" y1="${Y(o).toFixed(1)}" y2="${Y(o).toFixed(1)}" class="pg"/><text x="${PAD.l - 4}" y="${(Y(o) + 3).toFixed(1)}" class="pt" text-anchor="end">${fmt.num(Math.abs(o))}</text>`;
  const strip = sts.map((st, i) => { const [a, b] = half(i); return `<rect x="${a.toFixed(1)}" y="${PH - PAD.b + 4}" width="${Math.max(0.5, b - a).toFixed(1)}" height="5" fill="${ROAD_STATUS[st.status]?.color || '#cbd5e1'}"/>`; }).join('');
  const ends = routeEnds(r);
  return `<section class="card road-profile" data-open="true">
    <header class="card-head" style="cursor:default"><span class="card-ico">${icon('ruler-dimension-line')}</span><div style="min-width:0"><div class="card-title">Sơ đồ duỗi thẳng tuyến</div><div class="card-sub" data-road-readout>${readout(r, midStation(r) || sts[sts.length >> 1])}</div></div></header>
    <div class="card-content">
      <svg class="profile" viewBox="0 0 ${PW} ${PH}" role="img" aria-label="Chỉ giới đường đỏ quy hoạch và mép công trình hiện có hai bên tim đường, theo chiều dài tuyến" data-road-chart>
        <g class="pb">${band}</g><g class="pk">${bld}</g><g class="px">${enc}</g>
        ${tick(ymax)}${tick(ymax / 2)}${tick(-ymax / 2)}${tick(-ymax)}
        <line x1="${PAD.l}" x2="${PW - PAD.r}" y1="${mid}" y2="${mid}" class="pm"/>
        <path d="${red('dL', 1)}" class="pw"/><path d="${red('dR', -1)}" class="pw"/>
        <text x="${PAD.l + 3}" y="${PAD.t + 8}" class="pt ps">phía ${esc(S.dirL)}</text>
        <text x="${PAD.l + 3}" y="${PAD.t + H - 3}" class="pt ps">phía ${esc(S.dirR)}</text>
        <line x1="${X(0).toFixed(1)}" x2="${X(0).toFixed(1)}" y1="${PAD.t}" y2="${PH - PAD.b + 9}" class="p0"/>
        ${strip}
        <text x="${PAD.l}" y="${PH - 4}" class="pt">${esc(ends.a)}</text>
        <text x="${(X(0) > PW - 70 ? X(0) - 3 : X(0) + 3).toFixed(1)}" y="${X(0) < PAD.l + 80 ? mid - 4 : PAD.t + 8}" class="pt p0t" text-anchor="${X(0) > PW - 70 ? 'end' : 'start'}">điểm chọn</text>
        <text x="${PW - PAD.r}" y="${PH - 4}" class="pt" text-anchor="end">${esc(ends.b)}</text>
        <line x1="0" x2="0" y1="${PAD.t}" y2="${PH - PAD.b + 9}" class="pc" data-road-cursor style="display:none"/>
      </svg>
      <div class="plegend"><span><i class="lw"></i>Chỉ giới đường đỏ</span><span><i class="lb"></i>Đất giao thông QH</span><span><i class="le"></i>Công trình hiện có</span><span><i class="lx"></i>Phần nằm trong lộ giới</span></div>
    </div>
  </section>`;
}
function niceMax(v) { const st = v > 40 ? 20 : 10; return Math.ceil(v / st) * st; }
function routeEnds(r) {
  const sts = r.stations, a = sts[0].p, b = sts[sts.length - 1].p;
  const brg = ((Math.atan2((b[0] - a[0]) * Math.cos((a[1] * Math.PI) / 180), b[1] - a[1]) * 180) / Math.PI + 360) % 360;
  const C = ['Bắc', 'Đông Bắc', 'Đông', 'Đông Nam', 'Nam', 'Tây Nam', 'Tây', 'Tây Bắc'];
  const k = Math.round(brg / 45) % 8;
  return { a: `← ${C[(k + 4) % 8]}  ${fmt.num(sts[0].s)} m`, b: `+${fmt.num(sts[sts.length - 1].s)} m  ${C[k]} →` };
}
/** One-line reading of a station (profile hover). */
export function readout(r, st) {
  if (!st) return 'Rê chuột trên biểu đồ để xem từng mặt cắt';
  const S = r.summary;
  const pos = st.s === 0 ? 'Tại điểm chọn' : `${st.s > 0 ? '+' : '−'}${fmt.num(Math.abs(st.s))} m`;
  const bits = [];
  if (st.W != null && (st.status === 'keep' || st.status === 'widen')) bits.push(`lộ giới ≈ ${n1(st.W)} m`);
  if (st.bL != null && st.bR != null && (st.status === 'keep' || st.status === 'widen')) bits.push(`hiện trạng ≈ ${n1(st.bL + st.bR)} m`);
  if (st.status === 'widen') { const w = []; if (st.encL >= 1) w.push(`phía ${S.dirL} ${n1(st.encL)} m`); if (st.encR >= 1) w.push(`phía ${S.dirR} ${n1(st.encR)} m`); bits.push((r.kind === 'new' ? 'cắt vào công trình ' : 'mở rộng ') + w.join(', ')); }
  if (st.status === 'incell') bits.push('đường nằm trong ô ' + (st.cell?.name || 'đất'));
  if (st.status === 'junction') bits.push('nút giao');
  if (st.status === 'open' || st.status === 'none') bits.push(stLabel(r, st.status).toLowerCase());
  return `${pos}: ${esc(bits.join(' · '))}`;
}
/** Station index under a pointer x (0..1 of the chart width). */
export function stationAt(r, fx) {
  const sts = r.stations, x0 = sts[0].s, x1 = sts[sts.length - 1].s;
  const sPix = (PW * fx - PAD.l) / (PW - PAD.l - PAD.r);
  const s = x0 + sPix * (x1 - x0);
  let bi = 0;
  sts.forEach((t, i) => { if (Math.abs(t.s - s) < Math.abs(sts[bi].s - s)) bi = i; });
  return { i: bi, x: PAD.l + ((sts[bi].s - x0) / Math.max(1, x1 - x0)) * (PW - PAD.l - PAD.r) };
}

/* ---------------- details ---------------- */

function detailCard(r) {
  const S = r.summary, c = S.counts, rows = [];
  const tot = S.stations || 1;
  if (S.encL > 0) rows.push([`Mở rộng về phía ${S.dirL}`, `~${n1(S.encL)} m (tối đa ${n1(S.encMaxL)} m)`]);
  if (S.encR > 0) rows.push([`Mở rộng về phía ${S.dirR}`, `~${n1(S.encR)} m (tối đa ${n1(S.encMaxR)} m)`]);
  if (S.width) rows.push(['Lộ giới đo được', `${n1(S.width.min)} – ${n1(S.width.max)} m (${S.width.n} mặt cắt)`]);
  if (S.offset) rows.push(['Đất GT quy hoạch lệch khỏi tim đường', `~${n1(S.offset.d)} m về phía ${S.offset.dir}`]);
  rows.push(['Đoạn phân tích', `${fmt.num(Math.abs(r.span.from) + r.span.to)} m, mỗi 20 m một mặt cắt`]);
  const bar = Object.keys(ROAD_STATUS).filter((k) => c[k]).map((k) => `<i style="flex:${c[k]};background:${ROAD_STATUS[k].color}" data-tip="${esc(stLabel(r, k))}: ${pctOf(c[k], tot)}%"></i>`).join('');
  const leg = Object.keys(ROAD_STATUS).filter((k) => c[k]).map((k) => `<span><i style="background:${ROAD_STATUS[k].color}"></i>${esc(stLabel(r, k))} <b>${pctOf(c[k], tot)}%</b></span>`).join('');
  const projects = (S.projects || []).map((p) => `<div class="road-proj">${icon('drafting-compass', 'xs')}<span>${esc(p.name)}</span><em>${esc(LEVEL_SHORT[p.level] || '')}</em></div>`).join('');
  return `<section class="card" data-open="true">
    <header class="card-head" role="button" tabindex="0" aria-expanded="true"><span class="card-ico">${icon('list-checks')}</span><div style="min-width:0"><div class="card-title">Chi tiết theo đoạn</div><div class="card-sub">Tỷ lệ chiều dài theo hiện trạng so với quy hoạch</div></div>${icon('chevron-down', 'chev')}</header>
    <div class="card-body"><div class="card-inner"><div class="card-content">
      <div class="sbar">${bar}</div>
      <div class="slegend">${leg}</div>
      <div class="kv">${rows.map(([k, v]) => `<div>${esc(k)}</div><div>${esc(v)}</div>`).join('')}</div>
      ${projects ? `<div class="road-projs"><div class="cat-sub">Đồ án dùng để xác định chỉ giới</div>${projects}</div>` : ''}
    </div></div></div>
  </section>`;
}

function buildingsCard(r) {
  const B = r.buildings;
  if (!B || r.summary.mainLevel === 'general') return '';
  if (!B.count) return `<div class="note">${icon('circle-check')}<div>Không thấy công trình nào nằm trong phạm vi lộ giới quy hoạch trên đoạn phân tích (theo dữ liệu Overture Maps, ${fmt.num(r.footprints)} công trình quanh điểm).</div></div>`;
  const S = r.summary;
  const rows = B.top.slice(0, 8).map((b, i) => `<button type="button" class="snap-row" role="listitem" data-act="road-bld" data-i="${i}">
      <span class="swatch" style="--sw:#dc2626"></span>
      <span class="nm"><b>Công trình ~${fmt.num(b.area)} m²</b><span>phía ${esc(b.side === 'L' ? S.dirL : S.dirR)} · ${b.s === 0 ? 'tại điểm chọn' : `${b.s > 0 ? '+' : '−'}${fmt.num(Math.abs(b.s))} m dọc tuyến`}</span></span>
      <span class="val">~${fmt.num(b.areaIn, b.areaIn < 10 ? 1 : 0)} m²<small>${Math.round(b.share * 100)}%</small></span>${icon('chevron-right', 'sm')}
    </button>`).join('');
  return `<section class="card" data-open="true">
    <header class="card-head" role="button" tabindex="0" aria-expanded="true"><span class="card-ico">${icon('building')}</span><div style="min-width:0"><div class="card-title">Công trình nằm trong lộ giới</div><div class="card-sub">${B.count} công trình · ~${fmt.num(B.areaIn)} m² · ${B.significant} vướng đáng kể</div></div>${icon('chevron-down', 'chev')}</header>
    <div class="card-body"><div class="card-inner"><div class="card-content">
      <div class="snap" style="margin:0"><div class="snap-head"><span>Lớn nhất trước</span><span>diện tích trong lộ giới</span></div><div role="list" aria-label="Công trình nằm trong lộ giới">${rows}</div></div>
    </div></div></div>
  </section>`;
}

function legendCard(r) {
  const sw = (k) => { const m = k === 'new' ? NEW_ROUTE : ROAD_STATUS[k]; return `<span><i class="ln" style="background:${m.color}"></i>${esc(k === 'new' ? m.label : stLabel(r, k))}</span>`; };
  return `<div class="road-legend" aria-label="Chú giải trên bản đồ">
    <span><i class="band"></i>Phạm vi lộ giới quy hoạch</span>
    <span><i class="red"></i>Chỉ giới đường đỏ (ước tính)</span>
    <span><i class="bld"></i>Công trình nằm trong lộ giới</span>
    ${r.kind === 'new' ? sw('new') : ''}${sw('keep')}${sw('widen')}${r.kind === 'new' ? '' : sw('incell')}
  </div>`;
}

function disclaimer(r) {
  const noB = r.footprints == null && r.kind !== 'none';
  return `<div class="note">${icon('info')}<div>Hệ thống quy hoạch Hà Nội <b>chưa công bố lớp chỉ giới đường đỏ</b>. Lộ giới ở đây là khoảng trống giữa ranh các ô đất hai bên đường trong đồ án phân khu / chi tiết (đất giao thông); tim đường hiện trạng từ OpenStreetMap, công trình từ Overture Maps (OSM, Google, Microsoft). Sai số có thể vài mét; nơi chỉ có quy hoạch chung 1/10.000 chỉ mang tính định hướng.${noB ? ' <b>Chưa tải được dữ liệu công trình</b> nên chưa đánh giá được nhà bị ảnh hưởng.' : ''} Cần xin thông tin chỉ giới đường đỏ tại cơ quan quản lý quy hoạch trước khi giao dịch, xây dựng.<div class="meta-line">${icon('clock', 'xs')} ${fmt.ms(r.loadMs || 0)} · ${r.footprints != null ? `${fmt.num(r.footprints)} công trình` : 'không có dữ liệu công trình'} · phân tích ${fmt.ms(r.ms || 0)}</div></div></div>`;
}
